import { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const SCROLL_STEP = 120;

/**
 * Barra de pestañas horizontal con scroll y flechas (mismo criterio visual que ann
 * `TabListButton` / `tab.styled.js`: texto #3A3A3A, activo #2173FF + borde inferior 2px).
 *
 * Las flechas van en el flujo flex y solo si hay overflow. El swipe táctil funciona de forma nativa.
 *
 * @example
 * <ScrollTabs>
 *   <ScrollTabs.Item icon={Users} active onClick={…}>Users</ScrollTabs.Item>
 *   <ScrollTabs.Item icon={Shield} onClick={…}>Roles</ScrollTabs.Item>
 * </ScrollTabs>
 */
const ScrollTabs = ({ children, className = '' }) => {
  const navRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const check = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    check();
    const el = navRef.current;
    if (!el) return;
    el.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
      ro.disconnect();
    };
  }, [check]);

  const scroll = (dir) =>
    navRef.current?.scrollBy({ left: dir * SCROLL_STEP, behavior: 'smooth' });

  return (
    <div
      className={[
        'flex items-stretch border-b border-edge bg-surface',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {canScrollLeft && (
        <button
          onClick={() => scroll(-1)}
          className="flex shrink-0 items-center justify-center border-r border-edge px-1.5 text-content-faint active:bg-surface-hover"
          aria-label="Scroll left"
        >
          <ChevronLeft size={16} />
        </button>
      )}

      {/* scrollbar-hide: Firefox via scrollbarWidth, WebKit via global utility */}
      <nav
        ref={navRef}
        className="scrollbar-hide flex min-h-11 flex-1 items-end gap-0 overflow-x-auto px-1"
        style={{ scrollbarWidth: 'none' }}
      >
        {children}
      </nav>

      {canScrollRight && (
        <button
          onClick={() => scroll(1)}
          className="flex shrink-0 items-center justify-center border-l border-edge px-1.5 text-content-faint active:bg-surface-hover"
          aria-label="Scroll right"
        >
          <ChevronRight size={16} />
        </button>
      )}
    </div>
  );
};

const Item = ({ icon: IconComp, active = false, onClick, children, className = '' }) => {
  const btnRef = useRef(null);

  const handleClick = (e) => {
    onClick?.(e);
    requestAnimationFrame(() => {
      btnRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    });
  };

  return (
    <button
      ref={btnRef}
      onClick={handleClick}
      role="tab"
      aria-selected={active}
      type="button"
      className={[
        '-mb-px flex shrink-0 items-center gap-2 border-b-2 px-4 py-2 text-sm font-bold whitespace-nowrap transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-2',
        active
          ? 'border-[#2173FF] text-[#2173FF] hover:border-[#002F80] hover:text-[#002F80] dark:border-blue-400 dark:text-blue-400 dark:hover:border-blue-300 dark:hover:text-blue-300'
          : 'border-transparent text-[#3A3A3A] hover:text-[#5C5C5C] dark:text-neutral-300 dark:hover:text-neutral-200',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {IconComp && (
        <IconComp size={16} className="shrink-0 text-current" strokeWidth={2} />
      )}
      <span>{children}</span>
    </button>
  );
};

ScrollTabs.Item = Item;

export default ScrollTabs;
